/**
 * 智慧无限宏动作浏览器本地持久化。
 * 作为 macro_tab 的 UI 缓存：按当前设备的型号和 VID/PID 分开存放。
 */

import {
  applyZhwxMacroFixedInterval,
  buildZhwxMacroTab,
  getZhwxMacroLocalStorageKey,
  normalizeZhwxMacroActionsForDevice,
  ZHWX_MACRO_SLOT_COUNT,
  type ZhwxMacroAction,
  type ZhwxMacroIntervalMode,
} from "./zhwxMacro";

/** 与录制页 Action 结构一致，便于 JSON 序列化 */
export type ZhwxMacroLocalAction = {
  actionType: number;
  status: number;
  delay: number;
  keyCode: number;
  keyName?: string;
};

export type ZhwxMacroLocalSlotRecord = {
  actions: ZhwxMacroLocalAction[];
  valid: boolean;
  /** 间隔模式：0 录制延时 / 1 固定延时 */
  mode: number;
  /** 固定延时 ms 或默认间隔下的 repNum */
  repNum: number;
  updatedAt: number;
};

function isDemoMode(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem("isDemoMode") === "true";
}

function readLocalMap(): Record<string, ZhwxMacroLocalSlotRecord | null> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(getZhwxMacroLocalStorageKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeLocalMap(
  map: Record<string, ZhwxMacroLocalSlotRecord | null>
): void {
  if (typeof localStorage === "undefined" || isDemoMode()) return;
  try {
    localStorage.setItem(getZhwxMacroLocalStorageKey(), JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/** 宏槽是否已被用户清除（localStorage 显式置 null） */
export function isZhwxMacroLocalCleared(macroId: number): boolean {
  const map = readLocalMap();
  return map[String(macroId)] === null;
}

/** 读取单个宏槽本地录制数据 */
export function readZhwxMacroLocalSlot(
  macroId: number
): ZhwxMacroLocalSlotRecord | null {
  const map = readLocalMap();
  const record = map[String(macroId)];
  if (!record || typeof record !== "object") return null;
  if (!Array.isArray(record.actions)) return null;
  return record;
}

/** 写入单个宏槽本地录制数据 */
export function writeZhwxMacroLocalSlot(
  macroId: number,
  record: Omit<ZhwxMacroLocalSlotRecord, "updatedAt">
): void {
  if (isDemoMode()) return;
  const map = readLocalMap();
  map[String(macroId)] = {
    actions: Array.isArray(record.actions) ? record.actions : [],
    valid: Boolean(record.valid),
    mode: Number(record.mode) === 1 ? 1 : 0,
    repNum: Math.max(1, Number(record.repNum) || 1),
    updatedAt: Date.now(),
  };
  writeLocalMap(map);
}

/** 清除单个宏槽本地录制数据 */
export function clearZhwxMacroLocalSlot(macroId: number): void {
  if (isDemoMode()) return;
  const map = readLocalMap();
  map[String(macroId)] = null;
  writeLocalMap(map);
}

/** 写入设备 macro_tab 的单槽更新项 */
export type ZhwxMacroTabLocalUpdate = {
  macroId: number;
  actions: ZhwxMacroAction[];
  intervalMode: ZhwxMacroIntervalMode;
  fixedIntervalMs: number;
};

/** 将本地录制缓存转为写入设备的标准宏动作 */
export function localSlotToDeviceActions(
  local: ZhwxMacroLocalSlotRecord
): ZhwxMacroAction[] {
  const raw = (local.actions ?? [])
    .map((item) => ({
      actionType: Number(item.actionType) || 3,
      status: Number(item.status) ? 1 : 0,
      delay: Math.max(0, Math.round(Number(item.delay) || 0)),
      keyCode: Number(item.keyCode) || 0,
    }))
    .filter((item) => item.keyCode > 0);
  let actions = normalizeZhwxMacroActionsForDevice(raw);
  const intervalMode = local.mode === 1 ? 1 : 0;
  if (intervalMode === 1 && actions.length) {
    actions = applyZhwxMacroFixedInterval(actions, local.repNum);
  }
  return actions;
}

/** 读取单槽本地宏并转为可写入设备的动作（已规范化）；无有效动作时返回 [] */
export function getZhwxMacroDeviceActionsFromLocalSlot(
  macroId: number
): ZhwxMacroAction[] {
  if (isZhwxMacroLocalCleared(macroId)) return [];
  const local = readZhwxMacroLocalSlot(macroId);
  if (!local?.valid || !local.actions?.length) return [];
  return localSlotToDeviceActions(local);
}

/**
 * 收集所有本地有效槽，用于重建 macro_tab。
 * 避免 merge 设备读回的脏数据导致宏体损坏或无法播放。
 */
export function collectZhwxMacroTabUpdatesFromLocal(): ZhwxMacroTabLocalUpdate[] {
  const updates: ZhwxMacroTabLocalUpdate[] = [];

  for (let macroId = 0; macroId < ZHWX_MACRO_SLOT_COUNT; macroId++) {
    if (isZhwxMacroLocalCleared(macroId)) continue;
    const local = readZhwxMacroLocalSlot(macroId);
    if (!local?.valid || !local.actions?.length) continue;
    const actions = localSlotToDeviceActions(local);
    if (!actions.length) continue;
    updates.push({
      macroId,
      actions,
      intervalMode: (local.mode === 1 ? 1 : 0) as ZhwxMacroIntervalMode,
      fixedIntervalMs: Math.max(1, Number(local.repNum) || 1),
    });
  }
  return updates;
}

/** 基于本地缓存全量重建 macro_tab（null base，不 merge 设备数据） */
export function buildZhwxMacroTabFromLocalCache(): Uint8Array {
  return buildZhwxMacroTab(null, collectZhwxMacroTabUpdatesFromLocal());
}

/** 批量读取各槽摘要（actNum / valid），供宏列表回显 */
export function readZhwxMacroLocalSummaries(): Array<{
  macroId: number;
  actNum: number;
  valid: boolean;
  mode: number;
  repNum: number;
  actions: ZhwxMacroLocalAction[];
}> {
  return Array.from({ length: ZHWX_MACRO_SLOT_COUNT }, (_, macroId) => {
    const local = readZhwxMacroLocalSlot(macroId);
    const actions = local?.actions ?? [];
    const valid = Boolean(local?.valid && actions.length > 0);
    return {
      macroId,
      actNum: valid ? actions.length : 0,
      valid,
      mode: local?.mode ?? 0,
      repNum: local?.repNum ?? 1,
      actions: valid ? actions : [],
    };
  });
}
